class ProductRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );
  #mutationObserver = new MutationObserver((mutations) => {
    const configurationChanged = mutations.some(
      (mutation) => mutation.target === this &&
        mutation.type === 'attributes' &&
        mutation.oldValue !== this.getAttribute(mutation.attributeName)
    );
    if (!configurationChanged) return;

    this.dataset.recommendationsPerformed = 'false';
    this.classList.remove('hidden');
    delete this.dataset.error;
    this.#intersectionObserver.disconnect();
    this.#loadRecommendations();
  });
  #cachedRecommendations = {};
  #activeFetch = null;
  #requestVersion = 0;
  connectedCallback() {
    this.#intersectionObserver.observe(this);
    // Rendering and error handling update other attributes on this element.
    // Observe only request inputs so those updates cannot start a retry loop.
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['data-product-id', 'data-section-id', 'data-intent', 'data-url'],
    });
  }
  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#requestVersion += 1;
    this.#activeFetch?.abort();
  }
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;
    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }
    if (recommendationsPerformed === 'true') {
      return;
    }
    const requestVersion = ++this.#requestVersion;
    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (requestVersion !== this.#requestVersion || !this.isConnected) return;
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError' || requestVersion !== this.#requestVersion || !this.isConnected) return;
        this.#handleError(e);
      });
  }
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
